<template>
    <BlocksAnimation>
        <div
            class="mx-auto flex w-full cursor-pointer flex-col overflow-hidden lg:flex-row lg:justify-center"
            :class="[
                spaceBetweenClass,
                {
                    'rounded-image-default': block?.ContainerWidth !== 'full-width' && block?.SpaceBetween === 'none',
                    'border-neutral-subtle border lg:border-0': spaceBetweenClass === 'none',
                },
            ]"
        >
            <button
                v-for="(item, index) in block.ImageAccordionItems"
                :key="index"
                ref="accordionItem"
                class="group block w-full flex-auto transition-all duration-500 ease-in-out lg:h-[780px]"
                :class="{
                    'rounded-image-default overflow-hidden': block?.SpaceBetween !== 'none',
                    'lg:w-1/6': activeIndex !== index,
                    'lg:w-2/6': activeIndex === index,
                    'lg:w-3/6': (activeIndex === index) & (block?.AccordionWidth === 'wide'),
                    'lg:w-5/6': (activeIndex === index) & (block?.AccordionWidth === 'extra-wide'),
                }"
                @click="(e) => handleClick(e, index)"
            >
                <div class="relative grid h-full overflow-hidden">
                    <!--Image-->
                    <Image
                        class="absolute inset-0 order-2 col-[1/1] row-[1/1] w-full"
                        :image="item.imageAccordionItem_Image"
                    />

                    <!--Collapsed Content-->
                    <div
                        :data-mode="collapsedDataMode"
                        class="relative inset-0 isolate col-[1/1] row-[1/1] flex cursor-pointer justify-start p-6 py-8 text-left transition-opacity duration-150 after:absolute after:inset-0 after:z-[-1]"
                        :class="{
                            'order-3 opacity-100': activeIndex !== index,
                            'order-1 opacity-0': activeIndex === index,
                            'lg:text-left': headingVertical,
                            'lg:text-center': !headingVertical,
                            'lg:justify-center': block?.ContentPosition === 'centred',
                            'lg:items-end': block?.ContentPosition === 'bottom',
                            'after:surface-primary after:opacity-75':
                                collapsedDataMode !== 'brand-invert' && !block?.Gradient,
                            'after:bg-black after:opacity-30': collapsedDataMode === 'brand-invert' && !block?.Gradient,
                            'bg-overlay-gradient-default': collapsedDataMode !== 'brand-invert' && block?.Gradient,
                            'bg-overlay-gradient-dark': collapsedDataMode === 'brand-invert' && block?.Gradient,
                        }"
                    >
                        <div
                            class="relative flex w-full flex-row items-center gap-4 pr-14 lg:flex-col lg:justify-center lg:pr-0"
                            :class="{
                                'opacity-100 duration-500 lg:delay-300': activeIndex !== index,
                                'opacity-0': activeIndex === index,
                            }"
                        >
                            <div
                                v-if="item.imageAccordionItem_Icon && item.imageAccordionItem_Icon.name"
                                class="flex items-center justify-center"
                                :class="{
                                    'lg:order-2': headingVertical,
                                }"
                            >
                                <DIcon
                                    :icon="iconName(item.imageAccordionItem_Icon)"
                                    size="w-6 h-6 lg:w-12 lg:h-12"
                                    class="text-heading"
                                />
                            </div>
                            <div
                                :class="{
                                    'lg:-rotate-180 lg:[writing-mode:vertical-rl]': headingVertical,
                                }"
                            >
                                <p
                                    v-if="block.ShowSubheading"
                                    class="font-body text-subheading text-lg font-(--font-weight-subheading)"
                                >
                                    {{ item.imageAccordionItem_Subheading }}
                                </p>
                                <p
                                    class="text-neutral-strong font-brand-heading"
                                    :class="{
                                        'heading-md': item.imageAccordionItem_HeadingSize === 'medium',
                                        'heading-lg': item.imageAccordionItem_HeadingSize === 'large',
                                    }"
                                >
                                    {{ item.imageAccordionItem_Heading }}
                                </p>
                            </div>
                            <div class="absolute right-0 lg:hidden">
                                <DButton
                                    size="lg"
                                    icon="ph:caret-down"
                                    tag="div"
                                    theme="base"
                                    tabindex="-1"
                                />
                            </div>
                        </div>
                    </div>

                    <!--Expanded Content-->
                    <div
                        :data-mode="expandedDataMode"
                        class="inset-0 isolate col-[1/1] row-[1/1] grid transition-opacity delay-0 duration-100 max-lg:transition-[grid-template-rows] max-lg:duration-500"
                        :class="{
                            'order-1 grid-rows-[0fr] opacity-0': activeIndex !== index,
                            'order-3 grid-rows-[1fr] opacity-100': activeIndex === index,
                            'surface-primary': block?.HideImage,
                            'bg-brand-opacity-80': !block?.HideImage,
                        }"
                    >
                        <div
                            class="h-full overflow-hidden"
                            :class="{
                                'opacity-0': activeIndex !== index,
                                'opacity-100 duration-500 lg:delay-500': activeIndex === index,
                            }"
                        >
                            <div
                                class="grid h-full gap-2 text-left lg:flex lg:flex-col lg:gap-4"
                                :class="{
                                    'px-6 pt-4 pb-6 lg:p-10 lg:py-10': activeIndex === index,
                                    'lg:text-left': block?.ContentAlignment === 'left',
                                    'lg:items-center lg:text-center': block?.ContentAlignment === 'centred',
                                    'lg:justify-end': block?.ContentPositionExpanded !== 'centred',
                                    'lg:justify-center': block?.ContentPositionExpanded === 'centred',
                                }"
                            >
                                <div
                                    class="flex h-14 w-6 items-center justify-center lg:h-14 lg:w-14"
                                    :class="{
                                        'mx-auto': block.ContentAlignment === 'centred',
                                    }"
                                >
                                    <DIcon
                                        v-if="item.imageAccordionItem_Icon && item.imageAccordionItem_Icon.name"
                                        :icon="iconName(item.imageAccordionItem_Icon)"
                                        size="w-6 h-6 lg:w-12 lg:h-12"
                                        :class="{
                                            'shape-brand-accent': block?.HideImage,
                                            'shape-brand-strong': !block?.HideImage,
                                        }"
                                    />
                                </div>
                                <div class="grid gap-2">
                                    <p
                                        v-if="item.imageAccordionItem_Subheading"
                                        class="text-subheading font-body text-lg font-(--font-weight-subheading)"
                                    >
                                        {{ item.imageAccordionItem_Subheading }}
                                    </p>
                                    <h2
                                        ref="headingElement"
                                        class="lg:leading-4xl text-neutral-strong font-brand-heading"
                                        :class="{
                                            'heading-md': item.imageAccordionItem_HeadingSize === 'medium',
                                            'heading-lg': item.imageAccordionItem_HeadingSize === 'large',
                                        }"
                                    >
                                        {{ item.imageAccordionItem_Heading }}
                                    </h2>
                                </div>
                                <p
                                    v-if="item.imageAccordionItem_LeadText"
                                    class="lead-lead text-neutral-strong"
                                >
                                    {{ item.imageAccordionItem_LeadText }}
                                </p>
                                <p class="text-neutral-strong leading-base text-base text-balance">
                                    {{ item.imageAccordionItem_BodyText }}
                                </p>
                                <div
                                    v-if="item.imageAccordionItem_Button[0]"
                                    class="mt-2 inline-flex lg:mt-0"
                                >
                                    <NuxtLink
                                        v-if="item.imageAccordionItem_Button?.length > 0"
                                        :to="item.imageAccordionItem_Button[0].url"
                                        :target="item.imageAccordionItem_Button[0].target"
                                    >
                                        <DButton
                                            class="button-primary"
                                            :theme="buttonTheme(item.imageAccordionItem_Button[0].buttonStyle)"
                                            tag="div"
                                        >
                                            {{ item.imageAccordionItem_Button[0].text }}
                                        </DButton>
                                    </NuxtLink>
                                </div>
                            </div>
                        </div>
                        <div class="absolute top-6 right-6 lg:hidden">
                            <DButton
                                size="lg"
                                icon="ph:caret-up"
                                tag="div"
                                theme="base"
                                tabindex="-1"
                            />
                        </div>
                    </div>
                </div>
            </button>
        </div>
    </BlocksAnimation>
</template>

<script setup>
    import { DButton, DIcon } from '@digistorm/spark'

    const { iconName } = useIcons()

    const props = defineProps({
        block: Object,
    })

    const { buttonTheme } = useButtonTheme()

    const activeIndex = ref(1)

    const handleClick = (event, index) => {
        // If clicking inside an active panel, only allow if target is a link
        if (activeIndex.value === index) {
            const isClickingLink = event.target.closest('a')
            if (!isClickingLink) {
                event.preventDefault()
                event.stopPropagation()
                // Reset active index when clicking active item
                activeIndex.value = null
            }
            return
        }
        // Otherwise set the clicked item as active
        activeIndex.value = index
    }

    const onKeydown = (e) => {
        if (e.key === 'Escape') {
            activeIndex.value = null
        }
    }

    const headingVertical = computed(() => {
        return props.block.HeadingOrientation === 'vertical'
    })

    const collapsedDataMode = computed(() => {
        switch (props.block.Palette) {
            case 'color-subtle':
                return 'brand'
            case 'color-invert':
                return 'brand-invert'
            default:
                return props.block.Palette
        }
    })

    const expandedDataMode = computed(() => {
        if (props.block.HideImage !== true) {
            return 'brand-invert'
        } else {
            switch (props.block.ExpandedPalette) {
                case 'color-subtle':
                    return 'brand'
                case 'color-invert':
                    return 'brand-invert'
                default:
                    return props.block.ExpandedPalette
            }
        }
    })

    const spaceBetweenClass = computed(() => {
        switch (props.block?.SpaceBetween) {
            case 'none':
                return ''
            case 'sm':
                return 'gap-4'
            case 'md':
            default:
                return 'gap-6'
            case 'lg':
                return 'gap-8'
            case 'xl':
                return 'gap-10'
        }
    })

    onMounted(() => {
        window.addEventListener('keydown', onKeydown)
    })
</script>

<style scoped>
    /* Collapsed background colours with gradient */
    .bg-overlay-gradient-default {
        background:
            linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.2) 100%),
            linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.7) 100%);
    }
    .bg-overlay-gradient-dark {
        background:
            linear-gradient(180deg, rgba(0, 0, 0, 0) 35.5%, rgba(0, 0, 0, 0.7) 100%),
            linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 2, 71, 0.2) 100%);
    }
</style>
